import React from 'react';
import cn from 'classnames';
import type { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Button, { ILink } from 'tbk-components/src/components/Button';
import Image, { IImageProps } from 'tbk-components/src/components/Image';
import Breadcrumb from 'tbk-components/src/components/Breadcrumb';
import { bgColors, colorMap } from '../../utils';

import logo from '../../assets/logo-bus-md.svg';

export interface IHeroBannerProps extends IBaseElementProps {
	backgroundColor?: bgColors;
	preHeading?: string;
	heading?: string;
	button?: ILink;
	breadcrumbs?: ILink[];
	children?: any;
	image?: IImageProps;
	mobileImage?: IImageProps;
	/**
	 * Internal use
	 * @noUI
	 */
	isMainBanner?: boolean;
	/**
	 * Logo toggle
	 * @noUI
	 */
	showLogo?: boolean;
}

/**
 * Hero Banner
 * @block
 * @requiresHooks
 */
const HeroBanner: React.FC<IHeroBannerProps> = ({
	backgroundColor = 'light',
	breadcrumbs = [],
	className,
	classNames = [],
	children,
	heading,
	image,
	mobileImage,
	isMainBanner,
	button,
	preHeading,
	showLogo = false,
	...props
}) => {
	const leftAlign = !isMainBanner && !!image; // convert image to boolean
	return (
		<div
			className={
				className ||
				cn(
					'hero-banner',
					'container-fluid',
					'px-md-0',
					`bg-${colorMap[backgroundColor]}`,
					...classNames,
				)
			}
			{...props}
		>
			<div className={cn('row', 'mx-0', 'justify-content-center')}>
				<div
					className={cn(
						'hero-banner__content',
						leftAlign && 'hero-banner__content--md-left',
						image ? 'col-md-6' : 'col-md-7',
						backgroundColor === 'dark' && 'text-light',
					)}
				>
					{showLogo && (
						<Image
							src={logo}
							width={218}
							height={95}
							className="d-md-none"
						/>
					)}
					{preHeading && (
						<h2 className="hero-banner__pre-heading">
							{preHeading}
						</h2>
					)}

					{heading && (
						<h1 className="hero-banner__heading">{heading}</h1>
					)}

					{children}

					{button?.label?.length > 0 && (
						<div>
							<Button
								type={
									backgroundColor === 'light' ||
									backgroundColor === 'dark'
										? 'primary'
										: 'secondary'
								}
								size={'lg'}
								classNames={['mt-3']}
								{...button}
							/>
						</div>
					)}
					{breadcrumbs?.length > 0 && (
						<Breadcrumb
							// @ts-ignore
							links={breadcrumbs}
							classNames={[
								leftAlign
									? 'breadcrumb-container--md-left'
									: '',
								backgroundColor === 'dark' && 'text-light',
							]}
						/>
					)}
				</div>
				{image && (
					<div className={cn(
						'hero-banner__image hero-banner__image col-md-6',
						mobileImage
							? 'hero-banner__image--desktop'
							: '',
					)}>
						{showLogo && (
							<Image
								src={logo}
								width={320}
								height={139}
								className="d-none d-md-block"
							/>
						)}
						<Image {...image} className="image" />
					</div>
				)}
				{mobileImage && (
					<div className={cn('hero-banner__image hero-banner__image hero-banner__image--mobile', 'col-md-6')}>
						{showLogo && (
							<Image
								src={logo}
								width={320}
								height={139}
								className="d-none d-md-block"
							/>
						)}
						<Image {...mobileImage} className="image" />
					</div>
				)}
			</div>
		</div>
	);
};

export default HeroBanner;
